import { io } from "socket.io-client";
import { marked } from "marked";
const socket = io();

// --- TOAST HELPER ---
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  showCloseButton: true,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

function showToast(msg, icon = "success") {
  Toast.fire({ icon: icon, title: msg });
}

// --- STATE MANAGEMENT ---
const state = {
  currentUser: null, // { id, name, email, role: 'teacher' | 'student' }

  classes: [],
  students: [],
  groups: [],
  messages: [],
  readReceipts: [], // [{ user_id, class_id, last_read_msg_id }]
  pinnedMessages: [], // [{ id, messageId, classId, pinnedBy, pinnedAt }]

  currentClassId: null,
  activeChatId: null,
  appMode: "classroom", // 'classroom' | 'chat'
  currentTab: "students", // 'students' | 'groups'
  isSidebarExpanded: true,

  // Temp State for UI
  selectedId: null, // ID student or group selected for points
  selectedType: null, // 'student' | 'group'
  uploadedImages: [], // Images waiting to be sent
  onlineUserIds: [], // List of "role_id" strings
  selectedMentionIndex: 0, // For keyboard navigation
  searchTerm: "", // For message search

  // Temp State for Class Creation
  newClass: { icon: "Calculator", color: "bg-blue-100", text: "text-blue-600" },
};

// --- AUTHENTICATION ---
let loginRole = "student";

window.switchLoginTab = function (role) {
  loginRole = role;
  const btnT = document.getElementById("tab-login-teacher");
  const btnS = document.getElementById("tab-login-student");
  const formT = document.getElementById("form-teacher");
  const formS = document.getElementById("form-student");

  if (role === "teacher") {
    btnT.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition bg-white shadow-sm text-primary";
    btnS.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition text-slate-500 hover:text-slate-700";
    formT.classList.remove("hidden");
    formS.classList.add("hidden");
  } else {
    btnT.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition text-slate-500 hover:text-slate-700";
    btnS.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition bg-white shadow-sm text-primary";
    formT.classList.add("hidden");
    formS.classList.remove("hidden");
  }
};

window.handleLogin = function () {
  const email =
    loginRole === "teacher"
      ? document.getElementById("login-email").value
      : document.getElementById("login-student-email").value;
  const password =
    loginRole === "teacher" ? document.getElementById("login-pass").value : "";

  if (!email) return showToast("Please enter Email", "warning");
  if (loginRole === "teacher" && !password)
    return showToast("Please enter Password", "warning");

  socket.emit("login", { email, password, role: loginRole });
};

socket.on("login_success", (data) => {
  const user = data.user || data;
  const token = data.token;

  if (token) {
    localStorage.setItem("token", token);
  }

  state.currentUser = user;
  document.getElementById("modal-login").classList.add("hidden");

  if (user.role === "student") {
    const studentRecord = state.students.find((s) => s.email === user.email);
    if (studentRecord) {
      state.currentClassId = studentRecord.classId;
    }
  }

  renderAll();
  checkRolePermissions();
});

socket.on("login_fail", (msg) => {
  const err = document.getElementById("login-error");
  err.innerText = msg;
  err.classList.remove("hidden");
  showToast(msg, "error");
});

socket.on("token_invalid", () => {
  localStorage.removeItem("token");
  document.getElementById("modal-login").classList.remove("hidden");
  showToast("Session expired. Please login again.", "warning");
});

function checkRolePermissions() {
  if (!state.currentUser) return;

  const isTeacher = state.currentUser.role === "teacher";

  const addClassBtn = document.querySelector(
    'aside button[onclick="openAddClassModal()"]'
  );
  if (addClassBtn) addClassBtn.style.display = isTeacher ? "flex" : "none";

  const headerActions = document.getElementById("header-actions-classroom");
  if (headerActions) {
    if (!isTeacher) {
      headerActions.style.setProperty("display", "none", "important");
    } else {
      headerActions.style.removeProperty("display");
    }
  }

  const chatSettingsBtn = document.querySelector(
    'button[onclick="toggleChatSettings()"]'
  );
  if (chatSettingsBtn)
    chatSettingsBtn.style.display = isTeacher ? "flex" : "none";
}

// --- SOCKET LISTENERS ---
socket.on("init_data", (data) => {
  updateState(data);
  if (!state.currentClassId && state.classes.length > 0) {
    state.currentClassId = state.classes[0].id;
  }
  renderAll();
});

socket.on("refresh_data", (data) => {
  updateState(data);
  renderAll();
});

socket.on("online_users", (users) => {
  state.onlineUserIds = users;
  if (state.appMode === "chat" && state.activeChatId) renderMainView();
  if (
    !document.getElementById("modal-chat-info").classList.contains("hidden")
  ) {
    renderChatInfoList();
  }
});

function updateState(data) {
  state.classes = data.classes;
  state.students = data.students;
  state.groups = data.groups;
  state.messages = data.messages;
  state.readReceipts = data.readReceipts || [];
  state.pinnedMessages = data.pinnedMessages || [];

  // Removed automatic mark_read to prevent loop
}

// --- NEW SOCKET LISTENERS FOR INCREMENTAL UPDATES ---

socket.on("read_receipt_updated", (data) => {
  // Update local state
  const existing = state.readReceipts.find(
    (r) => r.class_id === data.classId && r.user_id === data.userId
  );
  if (existing) {
    existing.last_read_msg_id = data.lastReadMsgId;
  } else {
    state.readReceipts.push({
      user_id: data.userId,
      class_id: data.classId,
      last_read_msg_id: data.lastReadMsgId,
    });
  }

  // Only re-render sidebar to update badges
  renderSidebarList();
});

socket.on("new_message", (msg) => {
  // Check if message already exists to prevent duplicates
  if (state.messages.some((m) => m.id === msg.id)) return;

  state.messages.push(msg);

  if (state.appMode === "chat" && state.activeChatId === msg.classId) {
    appendMessage(msg);
    renderSidebarList(); // Update preview text
    socket.emit("mark_read", msg.classId); // Mark read since we are viewing it
  } else {
    renderSidebarList(); // Update unread count
  }
});

socket.on("message_deleted", (msgId) => {
  const idx = state.messages.findIndex((m) => m.id === msgId);
  if (idx !== -1) {
    state.messages[idx].deleted = true;
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.remove();
      // Also remove context menu if exists
      const menu = document.getElementById(`context-menu-${msgId}`);
      if (menu) menu.remove();
    }
  }

  // Remove from pinned messages if it was pinned
  const wasPinned = state.pinnedMessages.some((p) => p.messageId === msgId);
  if (wasPinned) {
    state.pinnedMessages = state.pinnedMessages.filter(
      (p) => p.messageId !== msgId
    );
    if (state.appMode === "chat") {
      renderPinnedMessages();
    }
  }
});

socket.on("message_recalled", (msgId) => {
  const idx = state.messages.findIndex((m) => m.id === msgId);
  if (idx !== -1) {
    state.messages[idx].recalled = true;
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      // Replace with recalled version
      const newHTML = createMessageHTML(state.messages[idx]);

      // Create a temp container to parse the HTML string
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = newHTML;

      // The newHTML contains the message div and potentially the context menu div (though recalled msgs usually don't have one)
      // We need to replace the message div `el` with the new message div from `tempDiv`
      const newMessageDiv = tempDiv.querySelector(`[id="msg-${msgId}"]`);

      if (newMessageDiv) {
        el.replaceWith(newMessageDiv);
      }

      // Also remove any existing context menu for this message
      const oldMenu = document.getElementById(`context-menu-${msgId}`);
      if (oldMenu) oldMenu.remove();
    }
  }
});

socket.on("message_pinned", (pin) => {
  console.log(pin);
  state.pinnedMessages.push(pin);
  if (state.appMode === "chat" && state.activeChatId === pin.classId) {
    renderPinnedMessages();
  }
});

socket.on("message_unpinned", ({ messageId, classId }) => {
  state.pinnedMessages = state.pinnedMessages.filter(
    (p) => !(p.messageId === messageId && p.classId === classId)
  );
  if (state.appMode === "chat" && state.activeChatId === classId) {
    renderPinnedMessages();
  }
});

function createMessageHTML(msg) {
  let isMe = false;
  if (state.currentUser) {
    if (state.currentUser.role === "teacher") {
      isMe = msg.type === "teacher";
    } else {
      if (msg.senderId) {
        isMe = msg.senderId === state.currentUser.id;
      } else {
        isMe = msg.type === "student" && msg.name === state.currentUser.name;
      }
    }
  }

  const isBot = msg.type === "bot";

  let avatar = isBot
    ? `https://api.dicebear.com/7.x/bottts/svg?seed=AI`
    : isMe
    ? `https://api.dicebear.com/7.x/notionists/svg?seed=Teacher`
    : `https://api.dicebear.com/7.x/fun-emoji/svg?seed=${msg.name}`;

  let displayHtml = msg.text.replace(
    /(@\w+)/g,
    '<span class="mention-tag">$1</span>'
  );

  if (isBot) {
    displayHtml = marked.parse(msg.text);
  }

  if (state.searchTerm) {
    const term = state.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${term})`, "gi");
    displayHtml = displayHtml.replace(
      regex,
      '<mark class="bg-yellow-200 text-slate-800 rounded px-0.5">$1</mark>'
    );
  }

  displayHtml = displayHtml.replace(
    /<span class="mention-tag">@ask<\/span>/g,
    '<span class="bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-bold border border-blue-200 inline-flex items-center gap-1"><i class="fa-solid fa-robot text-xs"></i> @ask</span>'
  );

  if (msg.recalled) {
    displayHtml = `<span class="italic text-slate-400 text-xs border border-slate-200 px-2 py-1 rounded-lg bg-slate-50 inline-block">${msg.text}</span>`;
  }

  let imgHtml = "";
  if (msg.images && msg.images.length > 0) {
    imgHtml = `<div class="flex flex-wrap gap-1 mt-2">`;
    msg.images.forEach((src) => {
      imgHtml += `<img src="${src}" onclick="openLightbox('${src}')" class="max-w-[150px] max-h-[150px] object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90 transition">`;
    });
    imgHtml += `</div>`;
  }

  let replyHtml = "";
  if (msg.replyTo) {
    replyHtml = `
            <div onclick="scrollToMessage('${
              msg.replyTo.id
            }')" class="cursor-pointer mb-1 pl-2 border-l-2 ${
      isMe ? "border-white/50 text-white/80" : "border-slate-300 text-slate-400"
    } text-xs truncate opacity-75 hover:opacity-100 transition-opacity" title="Click to view original message">
                <span class="font-bold">${msg.replyTo.name}</span>: ${
      msg.replyTo.text
    }
            </div>
        `;
  }

  // Context Menu HTML
  const contextMenuId = `context-menu-${msg.id}`;
  const contextMenuHtml = `
        <div id="${contextMenuId}" class="hidden fixed bg-white shadow-2xl border border-slate-200 rounded-xl py-2 z-[9999] min-w-[180px] animate-fade-in">
            <button onclick="replyMessage('${
              msg.id
            }'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-blue-600 flex items-center gap-3 text-sm transition">
                <i class="fa-solid fa-reply w-4"></i> Reply
            </button>
            ${
              state.currentUser && state.currentUser.role === "teacher"
                ? `<button onclick="pinMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-yellow-50 text-yellow-600 flex items-center gap-3 text-sm transition">
                    <i class="fa-solid fa-thumbtack w-4"></i> Pin Message
                </button>`
                : ""
            }
            ${
              isMe && !msg.recalled
                ? `<button onclick="recallMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-orange-50 text-orange-600 flex items-center gap-3 text-sm transition border-t border-slate-100">
                    <i class="fa-solid fa-rotate-left w-4"></i> Recall
                </button>`
                : ""
            }
            ${
              isMe && !msg.recalled
                ? `<button onclick="deleteMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-red-50 text-red-600 flex items-center gap-3 text-sm transition">
                    <i class="fa-solid fa-trash w-4"></i> Delete
                </button>`
                : ""
            }
            ${
              !isMe &&
              !msg.recalled &&
              state.currentUser &&
              state.currentUser.role === "teacher"
                ? `<button onclick="adminRecallMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-red-50 text-red-600 flex items-center gap-3 text-sm transition border-t border-slate-100">
                    <i class="fa-solid fa-ban w-4"></i> Remove
                </button>`
                : ""
            }
            <button onclick="copyMessage('${
              msg.id
            }'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-slate-600 flex items-center gap-3 text-sm transition border-t border-slate-100">
                <i class="fa-regular fa-copy w-4"></i> Copy
            </button>
        </div>
    `;

  return `
        <div id="msg-${msg.id}" data-message-id="${
    msg.id
  }" class="message-item flex gap-3 ${
    isMe ? "flex-row-reverse" : ""
  } animate-fade-in mb-3 group items-end transition-colors duration-1000 rounded-xl p-1 relative">
            <img src="${avatar}" class="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-100 shrink-0">
            <div class="max-w-[75%] flex flex-col ${
              isMe ? "items-end" : "items-start"
            } relative">
                <div class="text-[10px] text-slate-400 mb-1 px-1 flex ${
                  isMe ? "flex-row-reverse" : ""
                } gap-1">
                    <span class="font-bold text-slate-600">${msg.name}</span>
                </div>
                
                <div class="flex items-center gap-2 ${
                  isMe ? "flex-row-reverse" : ""
                } relative">
                    <div class="${
                      isMe
                        ? "bg-primary text-white rounded-tr-none"
                        : "bg-white text-slate-600 rounded-tl-none"
                    } p-3 rounded-2xl shadow-sm text-sm break-words transition-shadow relative">
                        ${replyHtml}
                        ${displayHtml}
                        ${imgHtml}
                    </div>
                </div>
            </div>
        </div>
        ${!msg.recalled ? contextMenuHtml : ""}
    `;
}

function appendMessage(msg) {
  const container = document.getElementById("messages-container");
  if (!container) return;

  const isNearBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight < 100;

  const html = createMessageHTML(msg);
  container.insertAdjacentHTML("beforeend", html);

  if (
    isNearBottom ||
    (state.currentUser && msg.senderId === state.currentUser.id)
  ) {
    window.scrollToBottom();
  }
}

function renderAll() {
  renderSidebarList();
  renderMainView();
  if (state.appMode === "chat" && state.activeChatId) {
    renderPinnedMessages();
  }
}

window.handleLogout = function () {
  Swal.fire({
    title: "Are you sure?",
    text: "You will be logged out!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, logout!",
    heightAuto: false,
  }).then((result) => {
    if (result.isConfirmed) {
      socket.emit("logout");
      localStorage.removeItem("token");
      state.currentUser = null;
      window.location.reload();
    }
  });
};

// --- CLASS CRUD ---
const availableIcons = [
  { id: "Calculator", icon: "fa-calculator" },
  { id: "Palette", icon: "fa-palette" },
  { id: "Globe", icon: "fa-earth-americas" },
  { id: "Book", icon: "fa-book" },
  { id: "Music", icon: "fa-music" },
  { id: "Sport", icon: "fa-futbol" },
  { id: "Science", icon: "fa-flask" },
  { id: "Computer", icon: "fa-laptop" },
];

const availableColors = [
  { bg: "bg-blue-100", text: "text-blue-600" },
  { bg: "bg-pink-100", text: "text-pink-600" },
  { bg: "bg-green-100", text: "text-green-600" },
  { bg: "bg-orange-100", text: "text-orange-600" },
  { bg: "bg-purple-100", text: "text-purple-600" },
  { bg: "bg-red-100", text: "text-red-600" },
  { bg: "bg-teal-100", text: "text-teal-600" },
  { bg: "bg-yellow-100", text: "text-yellow-600" },
];

window.openAddClassModal = function () {
  state.editingClassId = null;
  document.getElementById("modal-add-class").classList.remove("hidden");
  document.getElementById("input-class-name").value = "";
  document.getElementById("input-class-name").focus();

  // Update modal title and button
  document.querySelector("#modal-add-class h2").textContent = "Add New Class";
  document.querySelector(
    '#modal-add-class button[onclick="confirmAddClass()"]'
  ).textContent = "Add Class";

  state.newClass = {
    icon: "Calculator",
    color: "bg-blue-100",
    text: "text-blue-600",
  };
  renderClassCreationOptions();
};

window.openEditClass = function () {
  if (!state.currentClassId) return;

  // Close settings menu
  document.getElementById("class-settings-menu").classList.add("hidden");

  const currentClass = state.classes.find((c) => c.id === state.currentClassId);
  if (!currentClass) return;

  state.editingClassId = state.currentClassId;
  state.newClass = {
    icon: currentClass.icon,
    color: currentClass.color,
    text: currentClass.text,
  };

  document.getElementById("modal-add-class").classList.remove("hidden");
  document.getElementById("input-class-name").value = currentClass.name;
  document.getElementById("input-class-name").focus();

  // Update modal title and button
  document.querySelector("#modal-add-class h2").textContent = "Edit Class";
  document.querySelector(
    '#modal-add-class button[onclick="confirmAddClass()"]'
  ).textContent = "Save Changes";

  renderClassCreationOptions();
};

function renderClassCreationOptions() {
  const iconContainer = document.getElementById("class-icon-select");
  const colorContainer = document.getElementById("class-color-select");

  iconContainer.innerHTML = "";
  availableIcons.forEach((item) => {
    const isSelected = state.newClass.icon === item.id;
    iconContainer.innerHTML += `
            <div onclick="selectClassIcon('${
              item.id
            }')" class="w-10 h-10 min-w-[2.5rem] rounded-full bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer border-2 ${
      isSelected
        ? "border-primary text-primary bg-blue-50"
        : "border-transparent hover:bg-slate-200"
    } transition">
                <i class="fa-solid ${item.icon}"></i>
            </div>
        `;
  });

  colorContainer.innerHTML = "";
  availableColors.forEach((item) => {
    const isSelected = state.newClass.color === item.bg;
    colorContainer.innerHTML += `
            <div onclick="selectClassColor('${item.bg}', '${
      item.text
    }')" class="w-10 h-10 min-w-[2.5rem] rounded-full ${item.bg} ${
      item.text
    } flex items-center justify-center cursor-pointer border-2 ${
      isSelected ? "border-slate-600" : "border-transparent hover:scale-110"
    } transition">
                ${isSelected ? '<i class="fa-solid fa-check"></i>' : ""}
            </div>
        `;
  });
}

window.selectClassIcon = function (iconId) {
  state.newClass.icon = iconId;
  renderClassCreationOptions();
};

window.selectClassColor = function (bg, text) {
  state.newClass.color = bg;
  state.newClass.text = text;
  renderClassCreationOptions();
};

window.confirmAddClass = function () {
  const name = document.getElementById("input-class-name").value.trim();
  if (!name) {
    showToast("Please enter class name!", "warning");
    return;
  }

  const classData = {
    name: name,
    icon: state.newClass.icon,
    color: state.newClass.color,
    text: state.newClass.text,
  };

  if (state.editingClassId) {
    // Edit existing class
    classData.id = state.editingClassId;

    // Optimistic update
    const idx = state.classes.findIndex((c) => c.id === state.editingClassId);
    if (idx !== -1) {
      state.classes[idx] = { ...state.classes[idx], ...classData };
      renderAll();
    }

    socket.emit("update_class", classData);
    showToast("Class updated successfully!");
  } else {
    // Add new class
    socket.emit("add_class", classData);
    showToast("Class created successfully!");
  }

  closeModal("modal-add-class");
  state.editingClassId = null;
};

// --- INIT & RENDERERS ---
function init() {
  setupChatInputs();

  window.onpopstate = function (event) {
    handleRouting();
  };

  const token = localStorage.getItem("token");
  if (token) {
    socket.emit("login_with_token", token);
  } else {
    document.getElementById("modal-login").classList.remove("hidden");
  }

  handleRouting();
  setupModalClickOutside();

  // Auto-collapse sidebar on mobile
  if (window.innerWidth < 768) {
    const action_classroom = document.getElementById(
      "header-actions-classroom"
    );
    const action_chat = document.getElementById("header-actions-chat");
    const sb = document.getElementById("sidebar");
    const icon = document.getElementById("sidebar-toggle-icon");
    action_chat.classList.add("hidden");
    action_classroom.classList.add("hidden");
    sb.classList.remove("sidebar-expanded");
    sb.classList.add("sidebar-collapsed");
    icon.classList.add("rotate-180");
    state.isSidebarExpanded = false;

    document.querySelectorAll(".sidebar-text").forEach((el) => {
      el.classList.add("hidden");
      el.classList.add("opacity-0");
    });
  }

  const msgContainer = document.getElementById("messages-container");
  const btnScroll = document.getElementById("btn-scroll-bottom");
  if (msgContainer) {
    msgContainer.addEventListener("scroll", () => {
      const isNearBottom =
        msgContainer.scrollHeight -
          msgContainer.scrollTop -
          msgContainer.clientHeight <
        100;
      if (!isNearBottom) {
        btnScroll.classList.remove("hidden");
      } else {
        btnScroll.classList.add("hidden");
      }
    });
  }
}

function setupModalClickOutside() {
  const modals = document.querySelectorAll('[id^="modal-"]');
  modals.forEach((modal) => {
    modal.addEventListener("click", (e) => {
      if (e.target === modal && modal.id !== "modal-login") {
        closeModal(modal.id);
      }
    });
  });
}

function handleRouting() {
  const path = window.location.pathname;
  const parts = path.split("/").filter((p) => p);

  if (parts.length === 0) {
    state.currentClassId = null;
    state.activeChatId = null;
    switchMode("classroom", false);
  } else if (parts[0] === "classroom" && parts[1]) {
    const id = parts[1];
    if (id) {
      state.currentClassId = id;
      state.activeChatId = null;
      switchMode("classroom", false);
    }
  } else if (parts[0] === "chat" && parts[1]) {
    const id = parts[1];
    if (id) {
      state.activeChatId = id;
      state.currentClassId = null;
      switchMode("chat", false);
    }
  }
}

function updateUrl(mode, id) {
  let url = "/";
  if (id) {
    url = `/${mode}/${id}`;
  }
  window.history.pushState({}, "", url);
}

window.toggleSidebar = function () {
  const sb = document.getElementById("sidebar");
  const icon = document.getElementById("sidebar-toggle-icon");
  const backdrop = document.getElementById("sidebar-backdrop");
  state.isSidebarExpanded = !state.isSidebarExpanded;

  if (state.isSidebarExpanded) {
    sb.classList.remove("sidebar-collapsed");
    sb.classList.add("sidebar-expanded");
    icon.classList.remove("rotate-180");

    if (window.innerWidth < 768) {
      backdrop.classList.remove("hidden");
      setTimeout(() => backdrop.classList.remove("opacity-0"), 10);
    }

    document.querySelectorAll(".sidebar-text").forEach((el) => {
      el.classList.remove("hidden");
      setTimeout(() => el.classList.remove("opacity-0"), 50);
    });
  } else {
    sb.classList.remove("sidebar-expanded");
    sb.classList.add("sidebar-collapsed");
    icon.classList.add("rotate-180");

    backdrop.classList.add("opacity-0");
    setTimeout(() => backdrop.classList.add("hidden"), 300);

    document.querySelectorAll(".sidebar-text").forEach((el) => {
      el.classList.add("opacity-0");
      setTimeout(() => el.classList.add("hidden"), 200);
    });
  }
};

window.closeSidebarOnMobile = function () {
  if (window.innerWidth < 768 && state.isSidebarExpanded) {
    toggleSidebar();
  }
};

window.switchMode = function (mode, updateHistory = true) {
  state.appMode = mode;

  if (updateHistory) {
    if (mode === "classroom") {
      if (state.currentClassId) updateUrl("classroom", state.currentClassId);
      else updateUrl("classroom", null);
    } else {
      if (state.activeChatId) {
        updateUrl("chat", state.activeChatId);
        socket.emit("mark_read", state.activeChatId);
      } else updateUrl("chat", null);
    }
  }

  const btnClass = document.getElementById("mode-classroom");
  const btnChat = document.getElementById("mode-chat");
  const title = document.getElementById("sidebar-title");

  if (mode === "classroom") {
    btnClass.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2 bg-white shadow-sm text-primary";
    btnChat.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700";
    title.innerText = "CLASSROOM";
  } else {
    btnClass.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700";
    btnChat.className =
      "flex-1 py-2 rounded-lg text-sm font-bold transition flex items-center justify-center gap-2 bg-white shadow-sm text-primary";
    title.innerText = "MESSAGES";
  }

  // Update mobile menu
  updateMobileMenu();

  renderSidebarList();
  renderMainView();
};

window.toggleMobileMenu = function () {
  const menu = document.getElementById("mobile-menu");
  menu.classList.toggle("hidden");
};

function updateMobileMenu() {
  const classroomActions = document.getElementById("mobile-classroom-actions");
  const chatActions = document.getElementById("mobile-chat-actions");

  if (state.appMode === "classroom") {
    if (classroomActions) classroomActions.classList.remove("hidden");
    if (chatActions) chatActions.classList.add("hidden");
  } else {
    if (classroomActions) classroomActions.classList.add("hidden");
    if (chatActions) chatActions.classList.remove("hidden");
  }
}

// Close mobile menu when clicking outside
document.addEventListener("click", (e) => {
  const menu = document.getElementById("mobile-menu");
  const menuBtn = e.target.closest('button[onclick="toggleMobileMenu()"]');

  if (menu && !menu.contains(e.target) && !menuBtn) {
    menu.classList.add("hidden");
  }
});

function renderSidebarList() {
  const container = document.getElementById("class-list-container");
  container.innerHTML = "";

  state.classes.forEach((c) => {
    const isActive =
      state.appMode === "chat"
        ? c.id === state.activeChatId
        : c.id === state.currentClassId;
    let iconClass = "fa-school";
    if (c.icon === "Calculator") iconClass = "fa-calculator";
    if (c.icon === "Palette") iconClass = "fa-palette";
    if (c.icon === "Globe") iconClass = "fa-earth-americas";

    let subText = "12 Students";
    let unreadBadge = "";

    if (state.appMode === "chat") {
      const classMsgs = state.messages.filter((m) => m.classId === c.id);
      const lastMsg = classMsgs[classMsgs.length - 1];
      subText = lastMsg
        ? lastMsg.name + ": " + lastMsg.text.substring(0, 15) + "..."
        : "No messages yet";

      if (state.currentUser) {
        const receipt = state.readReceipts.find(
          (r) => r.class_id === c.id && r.user_id === state.currentUser.id
        );
        const lastReadId = receipt ? receipt.last_read_msg_id : null;

        let unreadCount = 0;
        if (!lastReadId) {
          unreadCount = classMsgs.length;
        } else {
          const idx = classMsgs.findIndex((m) => m.id === lastReadId);
          if (idx !== -1) {
            unreadCount = classMsgs.length - 1 - idx;
          } else {
            unreadCount = 0;
          }
        }

        if (state.activeChatId === c.id) unreadCount = 0;

        if (unreadCount > 0) {
          const displayCount = unreadCount > 9 ? "9+" : unreadCount;
          unreadBadge = `<div class="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">${displayCount}</div>`;
        }
      }
    } else {
      const count = state.students.filter((s) => s.classId === c.id).length;
      subText = `${count} Students`;
    }

    const html = `
            <div onclick="switchClass('${
              c.id
            }')" class="cursor-pointer w-full flex items-center p-2 rounded-xl transition group ${
      isActive ? "bg-slate-100" : "hover:bg-slate-50"
    } relative">
                <div class="w-10 h-10 min-w-[2.5rem] rounded-full ${c.color} ${
      c.text
    } flex items-center justify-center text-lg shadow-sm border-2 ${
      isActive ? "border-primary" : "border-transparent"
    }">
                    <i class="fa-solid ${iconClass}"></i>
                </div>
                <div class="ml-3 sidebar-text overflow-hidden">
                    <div class="font-bold text-sm text-slate-700 truncate">${
                      c.name
                    }</div>
                    <div class="text-[10px] text-slate-400 font-bold truncate">${subText}</div>
                </div>
                ${unreadBadge}
            </div>
        `;
    container.innerHTML += html;
  });
}

window.switchClass = function (id) {
  if (state.appMode === "chat") {
    state.activeChatId = id;
    updateUrl("chat", id);
    socket.emit("mark_read", id);
  } else {
    state.currentClassId = id;
    updateUrl("classroom", id);
  }
  renderSidebarList();
  renderMainView();
};

function renderMainView() {
  const viewClassroom = document.getElementById("view-classroom");
  const viewChat = document.getElementById("view-chat");
  const viewChatWelcome = document.getElementById("view-chat-welcome");
  const viewClassroomWelcome = document.getElementById(
    "view-classroom-welcome"
  );
  const headerActionsClass = document.getElementById(
    "header-actions-classroom"
  );
  const headerActionsChat = document.getElementById("header-actions-chat");
  const headerTitle = document.getElementById("header-class-name");
  const mobileIcon = document.getElementById("mobile-menu-icon");
  const mobileActionsClass = document.getElementById(
    "mobile-classroom-actions"
  );
  const mobileActionsChat = document.getElementById("mobile-chat-actions");
  if (state.appMode === "classroom") {
    if (state.currentClassId) {
      const currentClass = state.classes.find(
        (c) => c.id === state.currentClassId
      );
      if (currentClass) {
        headerTitle.innerText = currentClass.name;

        viewClassroom.classList.remove("hidden");
        if (viewClassroomWelcome) viewClassroomWelcome.classList.add("hidden");

        viewChat.classList.add("hidden");
        if (viewChatWelcome) viewChatWelcome.classList.add("hidden");

        // Show classroom actions (desktop only)
        if (window.innerWidth >= 768) {
          headerActionsClass.classList.remove("hidden");
          headerActionsClass.classList.remove("flex");
          headerActionsClass.classList.add("md:flex");
          // Hide chat actions completely
          headerActionsChat.classList.add("hidden");
          headerActionsChat.classList.remove("flex");
          headerActionsChat.classList.remove("md:flex");
        } else {
          mobileIcon.classList.remove("hidden");
        }
        renderClassroomGrid();
      }
    } else {
      headerTitle.innerText = "Classroom Management";
      viewClassroom.classList.add("hidden");
      if (viewClassroomWelcome) viewClassroomWelcome.classList.remove("hidden");
      viewChat.classList.add("hidden");
      if (viewChatWelcome) viewChatWelcome.classList.add("hidden");
      if (window.innerWidth < 768) {
        mobileIcon.classList.add("hidden");
      } else {
        headerActionsClass.classList.add("hidden");
        headerActionsClass.classList.remove("md:flex");
        headerActionsChat.classList.add("hidden");
        headerActionsChat.classList.remove("md:flex");
      }
    }
  } else {
    if (state.activeChatId) {
      const currentClass = state.classes.find(
        (c) => c.id === state.activeChatId
      );

      if (currentClass) {
        const classStudents = state.students.filter(
          (s) => s.classId === state.activeChatId
        );
        const totalMembers = classStudents.length + 1;

        let onlineCount = 0;
        classStudents.forEach((s) => {
          if (state.onlineUserIds.includes(`student_${s.id}`)) onlineCount++;
        });
        if (state.onlineUserIds.some((id) => id.startsWith("teacher_")))
          onlineCount++;

        headerTitle.innerHTML = `
                    <div class="flex flex-col">
                        <span>${currentClass.name}</span>
                        <span class="text-[10px] text-slate-400 font-normal flex items-center gap-1">
                            <span class="w-2 h-2 bg-green-500 rounded-full inline-block"></span> ${onlineCount} online / ${totalMembers} members
                        </span>
                    </div>
                `;

        viewClassroom.classList.add("hidden");
        if (viewClassroomWelcome) viewClassroomWelcome.classList.add("hidden");

        viewChat.classList.remove("hidden");
        viewChat.classList.add("flex");
        if (viewChatWelcome) viewChatWelcome.classList.add("hidden");
        // Hide classroom actions completely
        if (window.innerWidth >= 768) {
          headerActionsClass.classList.add("hidden");
          headerActionsClass.classList.remove("flex");
          headerActionsClass.classList.remove("md:flex");
          // Show chat actions (desktop only)
          headerActionsChat.classList.remove("hidden");
          headerActionsChat.classList.remove("flex");
          headerActionsChat.classList.add("md:flex");
        } else {
          mobileIcon.classList.remove("hidden");
        }
        renderMessages();
        renderPinnedMessages();
        checkChatPermissionUI();
      }
    } else {
      headerTitle.innerText = "Messages";
      viewClassroom.classList.add("hidden");
      if (viewClassroomWelcome) viewClassroomWelcome.classList.add("hidden");

      viewChat.classList.add("hidden");
      viewChat.classList.remove("flex");
      if (viewChatWelcome) viewChatWelcome.classList.remove("hidden");
      if (window.innerWidth < 768) {
        mobileIcon.classList.add("hidden");
      } else {
        headerActionsClass.classList.add("hidden");
        headerActionsClass.classList.remove("md:flex");
        headerActionsChat.classList.add("hidden");
        headerActionsChat.classList.remove("md:flex");
      }
    }
  }
  checkRolePermissions();
}

window.setTab = function (tab) {
  state.currentTab = tab;
  const btnS = document.getElementById("tab-students");
  const btnG = document.getElementById("tab-groups");
  if (tab === "students") {
    btnS.className =
      "px-4 py-1.5 bg-white rounded-md shadow-sm text-slate-700 transition";
    btnG.className =
      "px-4 py-1.5 text-slate-400 hover:text-slate-600 transition";
  } else {
    btnS.className =
      "px-4 py-1.5 text-slate-400 hover:text-slate-600 transition";
    btnG.className =
      "px-4 py-1.5 bg-white rounded-md shadow-sm text-slate-700 transition";
  }
  renderClassroomGrid();
};

function formatStudentName(student) {
  const parts = student.name.trim().split(/\s+/);
  if (parts.length <= 1) return student.name;

  const firstName = parts[parts.length - 1];
  const lastName = parts[0];
  return `${firstName} ${lastName}`;
}

function renderClassroomGrid() {
  const grid = document.getElementById("classroom-grid");
  grid.innerHTML = "";

  const currentClassId = state.currentClassId;

  if (state.currentTab === "students") {
    const students = state.students.filter((s) => s.classId === currentClassId);

    students.forEach((s) => {
      const displayName = formatStudentName(s);
      const nicknameHtml = s.nickname
        ? `<div class="text-xs text-slate-500 font-semibold truncate w-full text-center mt-0.5">(${s.nickname})</div>`
        : "";

      grid.innerHTML += `
                <div id="card-student-${s.id}" onclick="openPointModal('${s.id}', 'student')" class="group bg-white rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-300 p-4 flex flex-col items-center cursor-pointer border-2 border-transparent hover:border-primary/20 relative active:scale-95 select-none">
                    <div id="badge-student-${s.id}" class="absolute top-3 right-3 bg-success text-white font-bold w-8 h-8 flex items-center justify-center rounded-full border-2 border-white shadow-md z-10 transition-transform">
                        ${s.points}
                    </div>
                    <img src="https://api.dicebear.com/7.x/fun-emoji/svg?seed=${s.seed}" class="w-20 h-20 md:w-24 md:h-24 mb-3 drop-shadow-md transition-transform group-hover:scale-110 duration-300">
                    <h3 class="font-bold text-slate-700 text-lg truncate w-full text-center" title="${s.name}">${displayName}</h3>
                    ${nicknameHtml}
                </div>
            `;
    });
  } else {
    const groups = state.groups.filter((g) => g.classId === currentClassId);
    groups.forEach((g) => {
      let totalP = 0;
      g.members.forEach((mid) => {
        const st = state.students.find((s) => s.id === mid);
        if (st) totalP += st.points;
      });

      grid.innerHTML += `
                <div id="card-group-${g.id}" onclick="openPointModal('${g.id}', 'group')" class="group bg-indigo-50 border-2 border-indigo-100 rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-300 p-4 flex flex-col items-center cursor-pointer relative active:scale-95 select-none">
                    <div id="badge-group-${g.id}" class="absolute top-3 right-3 bg-indigo-500 text-white font-bold w-8 h-8 flex items-center justify-center rounded-full border-2 border-white shadow-md z-10 transition-transform">
                        ${totalP}
                    </div>
                    <div class="w-20 h-20 md:w-24 md:h-24 mb-3 relative flex items-center justify-center">
                        <div class="absolute w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm top-0 left-0 z-10 text-2xl">🐯</div>
                        <div class="absolute w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm bottom-0 right-0 z-20 text-2xl">🐼</div>
                    </div>
                    <h3 class="font-bold text-indigo-700 text-lg truncate w-full text-center">${g.name}</h3>
                    <div class="text-xs font-bold text-indigo-400">${g.members.length} members</div>
                </div>
            `;
    });
  }
}

window.switchPointTab = function (tab) {
  const btnPos = document.getElementById("btn-tab-positive");
  const btnNeg = document.getElementById("btn-tab-negative");
  const viewPos = document.getElementById("points-positive");
  const viewNeg = document.getElementById("points-negative");

  if (tab === "positive") {
    btnPos.className =
      "px-4 py-2 rounded-lg font-bold bg-primary text-white transition";
    btnNeg.className =
      "px-4 py-2 rounded-lg font-bold text-slate-400 hover:bg-slate-100 transition";
    viewPos.classList.remove("hidden");
    viewNeg.classList.add("hidden");
  } else {
    btnPos.className =
      "px-4 py-2 rounded-lg font-bold text-slate-400 hover:bg-slate-100 transition";
    btnNeg.className =
      "px-4 py-2 rounded-lg font-bold bg-red-500 text-white transition";
    viewPos.classList.add("hidden");
    viewNeg.classList.remove("hidden");
  }
};

window.openPointModal = function (id, type) {
  if (state.currentUser && state.currentUser.role !== "teacher") return;

  state.selectedId = id;
  state.selectedType = type;
  const modal = document.getElementById("modal-points");
  const editGroupActions = document.getElementById("group-edit-actions");

  switchPointTab("positive");

  const avatar = document.getElementById("mp-avatar");
  const name = document.getElementById("mp-name");
  const subtitle = document.getElementById("mp-subtitle");
  const badge = document.getElementById("mp-badge");
  const currentPoints = document.getElementById("mp-current-points");

  if (type === "student") {
    const s = state.students.find(
      (x) => x.id === id && x.classId === state.currentClassId
    );
    if (!s) return; // Should not happen
    avatar.src = `https://api.dicebear.com/7.x/fun-emoji/svg?seed=${s.seed}`;
    name.innerText = s.name;
    subtitle.innerText = "STUDENT";
    badge.innerText = s.points;
    currentPoints.innerText = s.points;
    editGroupActions.classList.add("hidden");
  } else {
    const g = state.groups.find((x) => x.id === id);
    avatar.src = `https://api.dicebear.com/7.x/shapes/svg?seed=${g.name}`;
    name.innerText = g.name;
    subtitle.innerText = `GROUP (${g.members.length} MEMBERS)`;
    let tp = 0;
    g.members.forEach(
      (m) => (tp += state.students.find((s) => s.id === m).points)
    );
    badge.innerText = tp;
    currentPoints.innerText = tp;
    editGroupActions.classList.remove("hidden");
  }

  modal.classList.remove("hidden");
};

window.givePoint = function (amount) {
  let targetIdPrefix = "";
  let targetId = 0;

  if (state.selectedType === "student") {
    const s = state.students.find((x) => x.id === state.selectedId);
    targetIdPrefix = "student";
    targetId = s.id;
  } else {
    const g = state.groups.find((x) => x.id === state.selectedId);
    targetIdPrefix = "group";
    targetId = g.id;
  }

  // Optimistic update - update modal badge immediately
  const badge = document.getElementById("mp-badge");
  const currentPointsDisplay = document.getElementById("mp-current-points");
  const currentPoints = parseInt(badge.innerText) || 0;
  const newPoints = currentPoints + amount;
  badge.innerText = newPoints;
  currentPointsDisplay.innerText = newPoints;

  // Animate the badge
  badge.classList.add("scale-125", "rotate-12");
  setTimeout(() => {
    badge.classList.remove("scale-125", "rotate-12");
  }, 300);

  // Animate current points display
  currentPointsDisplay.classList.add("scale-110");
  currentPointsDisplay.style.color = amount > 0 ? "#10b981" : "#ef4444";
  setTimeout(() => {
    currentPointsDisplay.classList.remove("scale-110");
    currentPointsDisplay.style.color = "";
  }, 500);

  // Show floating point on badge
  showFloatingPointOnElement("mp-badge", amount);

  // Updated to send classId
  socket.emit("give_point", {
    id: state.selectedId,
    type: state.selectedType,
    amount: amount,
    classId: state.currentClassId,
  });

  setTimeout(() => {
    playPopAnimation(`badge-${targetIdPrefix}-${targetId}`);
    showFloatingPoint(`card-${targetIdPrefix}-${targetId}`, amount);
  }, 200);
};

function showFloatingPointOnElement(elementId, amount) {
  const element = document.getElementById(elementId);
  if (!element) return;

  // Get the element's position relative to viewport
  const rect = element.getBoundingClientRect();

  // Create floater in body to avoid layout shift
  const floater = document.createElement("div");
  floater.className = "floating-point";
  floater.innerText = amount > 0 ? `+${amount}` : amount;
  floater.style.position = "fixed";
  floater.style.left = `${rect.left + rect.width / 2}px`;
  floater.style.top = `${rect.top + rect.height / 2}px`;
  floater.style.transform = "translate(-50%, -50%)";
  floater.style.fontSize = "1.5rem";
  floater.style.fontWeight = "bold";
  floater.style.color = amount > 0 ? "#10b981" : "#ef4444";
  floater.style.pointerEvents = "none";
  floater.style.zIndex = "9999";
  floater.style.textShadow = "0 0 10px rgba(255,255,255,0.8)";

  document.body.appendChild(floater);

  setTimeout(() => {
    floater.style.transition = "all 1s ease-out";
    floater.style.opacity = "0";
    floater.style.top = `${rect.top - 100}px`;
  }, 100);

  setTimeout(() => floater.remove(), 1100);
}

function showFloatingPoint(cardId, amount) {
  const card = document.getElementById(cardId);
  if (!card) return;

  const el = document.createElement("div");
  el.className = `absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold text-4xl z-50 pointer-events-none animate-float-up ${
    amount > 0 ? "text-green-500" : "text-red-500"
  }`;
  el.innerText = (amount > 0 ? "+" : "") + amount;

  card.appendChild(el);

  setTimeout(() => {
    el.remove();
  }, 1000);
}

function playPopAnimation(elementId) {
  const el = document.getElementById(elementId);
  if (el) {
    el.classList.remove("animate-pop-up");
    void el.offsetWidth;
    el.classList.add("animate-pop-up");
  }
}

window.openAddModal = function () {
  if (state.currentTab === "students") {
    document.getElementById("modal-add-student").classList.remove("hidden");
    document.getElementById("input-student-name").value = "";
    document.getElementById("input-student-name").focus();
  } else {
    setupGroupModal(null);
  }
};

window.openEditGroupModal = function () {
  closeModal("modal-points");
  const groupId = state.selectedId;
  const group = state.groups.find((g) => g.id === groupId);
  setupGroupModal(group);
};

function setupGroupModal(group) {
  const isEdit = !!group;
  const modalTitle = document.querySelector("#modal-add-group h2");
  const btnConfirm = document.querySelector(
    '#modal-add-group button[onclick="confirmAddGroup()"]'
  );
  const inputName = document.getElementById("input-group-name");

  modalTitle.innerText = isEdit ? "Edit Group" : "Create New Group";
  btnConfirm.innerText = isEdit ? "Save Changes" : "Create Group";
  btnConfirm.onclick = isEdit
    ? () => confirmEditGroup(group.id)
    : confirmAddGroup;
  inputName.value = isEdit ? group.name : "";
  document.getElementById("search-group-members").value = "";

  // Get all students in current class
  const classStudents = state.students.filter(
    (s) => s.classId === state.currentClassId
  );

  // Get students already in groups (except current group if editing)
  const studentsInGroups = new Set();
  state.groups.forEach((g) => {
    if (g.classId === state.currentClassId && (!isEdit || g.id !== group.id)) {
      g.members.forEach((memberId) => studentsInGroups.add(memberId));
    }
  });

  // Filter to only show students NOT in any group (or in current group if editing)
  const availableStudents = classStudents.filter((s) => {
    if (isEdit && group.members.includes(s.id)) return true; // Can keep current members
    return !studentsInGroups.has(s.id); // Only students not in any group
  });

  // Store for search filtering
  window.currentAvailableStudents = availableStudents;
  window.currentEditingGroup = isEdit ? group : null;

  renderGroupStudentList(availableStudents, isEdit ? group : null);
  updateSelectedCount();

  document.getElementById("modal-add-group").classList.remove("hidden");
}

function renderGroupStudentList(students, editingGroup = null) {
  const list = document.getElementById("group-student-list");

  if (students.length === 0) {
    list.innerHTML = `
            <div class="col-span-2 text-center py-8 text-slate-400">
                <i class="fa-solid fa-users-slash text-4xl mb-2"></i>
                <p class="font-semibold">No available students</p>
                <p class="text-xs">All students are already in groups</p>
            </div>
        `;
    return;
  }

  list.innerHTML = students
    .map((s) => {
      const isChecked = editingGroup && editingGroup.members.includes(s.id);
      return `
            <label class="flex items-center p-2 border border-slate-100 rounded-lg cursor-pointer hover:bg-slate-50 transition student-item" data-student-name="${s.name.toLowerCase()}">
                <input type="checkbox" value="${s.id}" ${
        isChecked ? "checked" : ""
      } onchange="updateSelectedCount()" class="w-4 h-4 text-primary rounded focus:ring-primary group-checkbox">
                <span class="ml-2 text-sm font-bold text-slate-600">${
                  s.name
                }</span>
            </label>
        `;
    })
    .join("");
}

window.filterGroupMembers = function (searchText) {
  const normalizedSearch = searchText.toLowerCase().trim();
  const items = document.querySelectorAll(".student-item");

  items.forEach((item) => {
    const studentName = item.getAttribute("data-student-name");
    if (studentName.includes(normalizedSearch)) {
      item.style.display = "flex";
    } else {
      item.style.display = "none";
    }
  });
};

window.updateSelectedCount = function () {
  const checked = document.querySelectorAll(".group-checkbox:checked").length;
  const countEl = document.getElementById("selected-count");
  if (countEl) {
    countEl.textContent = `${checked} selected`;
  }
};

window.closeModal = function (id) {
  const element = document.getElementById(id);
  if (element) {
    element.classList.add("hidden");
  }
};

// Setup modal click outside handlers
(function setupModalHandlers() {
  if (window.__modalHandlersAdded) return;
  window.__modalHandlersAdded = true;

  document.addEventListener("click", (e) => {
    // Close modal when clicking directly on the modal backdrop (not on content inside)
    if (e.target.id && e.target.id.startsWith("modal-")) {
      const modal = e.target;
      if (
        modal.classList.contains("fixed") &&
        modal.classList.contains("inset-0")
      ) {
        closeModal(e.target.id);
      }
    }
  });
})();

window.confirmAddStudent = function () {
  const text = document.getElementById("input-student-name").value.trim();
  if (!text) return;

  if (!state.currentClassId) {
    alert("Please select a class first!");
    return;
  }

  const lines = text
    .split("\n")
    .map((n) => n.trim())
    .filter((n) => n);

  lines.forEach((line) => {
    const parts = line.split(" ");
    let email = "";
    let name = line;

    if (parts.length > 1 && parts[parts.length - 1].includes("@")) {
      email = parts.pop();
      name = parts.join(" ");
    }

    const newS = {
      classId: state.currentClassId,
      name: name,
      email: email || null,
      seed: name + Date.now(),
    };
    console.log("Emitting add_student:", newS);
    socket.emit("add_student", newS);
  });

  document.getElementById("input-student-name").value = "";
  closeModal("modal-add-student");
};

window.confirmAddGroup = function () {
  const name = document.getElementById("input-group-name").value.trim();
  const cbs = document.querySelectorAll(".group-checkbox:checked");
  if (!name || cbs.length === 0) {
    alert("Please enter name and select at least 1 student!");
    return;
  }

  const members = Array.from(cbs).map((cb) => cb.value);
  const newG = {
    classId: state.currentClassId,
    name: name,
    members: members,
  };
  socket.emit("add_group", newG);
  closeModal("modal-add-group");
};

window.confirmEditGroup = function (groupId) {
  const name = document.getElementById("input-group-name").value.trim();
  const cbs = document.querySelectorAll(".group-checkbox:checked");
  if (!name || cbs.length === 0) {
    alert("Please enter name and select at least 1 student!");
    return;
  }

  const members = Array.from(cbs).map((cb) => cb.value);
  const group = {
    id: groupId,
    name: name,
    members: members,
  };

  socket.emit("update_group", group);
  closeModal("modal-add-group");
};

window.toggleClassSettingsMenu = function () {
  const menu = document.getElementById("class-settings-menu");
  menu.classList.toggle("hidden");
};

// Close menu when clicking outside
document.addEventListener("click", function (e) {
  const menu = document.getElementById("class-settings-menu");
  const btn = document.getElementById("btn-class-settings");
  if (menu && btn && !menu.contains(e.target) && !btn.contains(e.target)) {
    menu.classList.add("hidden");
  }
});

window.openClassMembers = function () {
  if (!state.currentClassId) return;

  // Close the dropdown menu
  document.getElementById("class-settings-menu").classList.add("hidden");

  const membersList = document.getElementById("class-members-list");
  const students = state.students.filter(
    (s) => s.classId === state.currentClassId
  );

  if (students.length === 0) {
    membersList.innerHTML = `
            <div class="text-center py-8 text-slate-400">
                <i class="fa-solid fa-users text-4xl mb-2"></i>
                <p class="font-semibold">No students in this class yet</p>
                <p class="text-sm">Click "Add" button to add students</p>
            </div>
        `;
  } else {
    membersList.innerHTML = students
      .map(
        (s) => `
            <div class="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition">
                <div class="flex items-center gap-3 flex-1">
                    <img src="https://api.dicebear.com/7.x/fun-emoji/svg?seed=${
                      s.seed
                    }" class="w-10 h-10 rounded-full bg-white border-2 border-slate-200">
                    <div class="flex-1">
                        <div class="font-bold text-slate-700">${s.name}</div>
                        <div class="text-xs text-slate-500">
                            ${
                              s.nickname
                                ? `<span class="font-bold text-blue-600">(${s.nickname})</span>`
                                : ""
                            }
                        </div>
                        <div class="text-xs ${
                          s.email ? "text-green-600" : "text-orange-500"
                        } font-semibold">
                            ${
                              s.email
                                ? `<i class="fa-solid fa-check-circle"></i> Linked: ${s.email}`
                                : '<i class="fa-solid fa-exclamation-circle"></i> Not linked yet'
                            }
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    <button onclick="editStudentNickname('${
                      s.id
                    }')" class="px-3 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-50 transition" title="Edit Nickname">
                        <i class="fa-solid fa-tag"></i>
                    </button>
                    ${
                      !s.email
                        ? `
                        <button onclick="addEmailToStudent('${s.id}')" class="px-3 py-1 bg-primary text-white text-xs font-bold rounded-lg hover:bg-blue-500 transition">
                            <i class="fa-solid fa-envelope"></i> Add Email
                        </button>
                    `
                        : `
                        <button onclick="editStudentEmail('${s.id}')" class="px-3 py-1 bg-slate-200 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-300 transition">
                            <i class="fa-solid fa-pen"></i> Edit
                        </button>
                    `
                    }
                </div>
            </div>
        `
      )
      .join("");
  }

  document.getElementById("modal-class-settings").classList.remove("hidden");
};

window.editStudentNickname = async function (studentId) {
  const student = state.students.find((s) => s.id === studentId);
  if (!student) return;

  const { value: nickname } = await Swal.fire({
    title: "Edit Nickname",
    input: "text",
    inputValue: student.nickname || "",
    inputLabel: `Nickname for ${student.name} in this class`,
    inputPlaceholder: "Enter nickname",
    showCancelButton: true,
    confirmButtonText: "Save",
    confirmButtonColor: "#3b82f6",
  });

  if (nickname !== undefined) {
    // Allow empty string to clear nickname
    socket.emit("update_student_nickname", {
      studentId,
      nickname,
      classId: state.currentClassId,
    });

    // Optimistic update
    student.nickname = nickname;
    openClassMembers(); // Re-render list
    renderClassroomGrid(); // Re-render grid
    showToast("Nickname updated!");
  }
};

window.addEmailToStudent = async function (studentId) {
  const { value: email } = await Swal.fire({
    title: "Add Email",
    input: "email",
    inputLabel: "Enter student email address",
    inputPlaceholder: "student@example.com",
    showCancelButton: true,
    confirmButtonText: "Add Email",
    confirmButtonColor: "#3b82f6",
    inputValidator: (value) => {
      if (!value) {
        return "Please enter an email address";
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return "Please enter a valid email address";
      }
    },
  });

  if (email) {
    socket.emit("update_student_email", {
      studentId,
      email,
      classId: state.currentClassId,
    });
    showToast("Email added successfully!");
  }
};

window.editStudentEmail = async function (studentId) {
  const student = state.students.find((s) => s.id === studentId);
  if (!student) return;

  const { value: email } = await Swal.fire({
    title: "Edit Email",
    input: "email",
    inputValue: student.email,
    inputLabel: "Enter new email address",
    showCancelButton: true,
    confirmButtonText: "Update Email",
    confirmButtonColor: "#3b82f6",
    inputValidator: (value) => {
      if (!value) {
        return "Please enter an email address";
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return "Please enter a valid email address";
      }
    },
  });

  if (email && email !== student.email) {
    socket.emit("update_student_email", {
      studentId,
      email,
      classId: state.currentClassId,
    });
    showToast("Email updated successfully!");
  }
};

window.resetClassPoints = function () {
  // Close the dropdown menu
  document.getElementById("class-settings-menu").classList.add("hidden");

  Swal.fire({
    title: "Are you sure?",
    text: "Reset points for ALL students in this class to 0? This cannot be undone.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f97316",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, reset points!",
  }).then((result) => {
    if (result.isConfirmed) {
      socket.emit("reset_points", state.currentClassId);
      showToast("Points reset successfully!");
    }
  });
};

window.deleteClass = function () {
  // Close the dropdown menu
  document.getElementById("class-settings-menu").classList.add("hidden");

  Swal.fire({
    title: "Are you sure?",
    text: "PERMANENTLY DELETE this class? All students, groups, and messages will be deleted. This cannot be undone!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it!",
  }).then((result) => {
    if (result.isConfirmed) {
      socket.emit("delete_class", state.currentClassId);
      state.currentClassId = null;
      state.activeChatId = null;
      closeModal("modal-class-settings");
      showToast("Class deleted successfully!");
    }
  });
};

function setupChatInputs() {
  const input = document.getElementById("chat-input");
  if (!input) return;

  input.addEventListener("keydown", (e) => {
    const suggestions = document.getElementById("mention-suggestions");
    const isSuggestionsVisible = !suggestions.classList.contains("hidden");

    if (isSuggestionsVisible) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        state.selectedMentionIndex++;
        renderMentionSuggestions();
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        state.selectedMentionIndex = Math.max(
          0,
          state.selectedMentionIndex - 1
        );
        renderMentionSuggestions();
        return;
      }
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault();
        insertMention("ask");
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      closeMentionSuggestions();
    }
  });

  input.addEventListener("input", (e) => {
    const val = input.value;
    const cursorPosition = input.selectionStart;
    const textBeforeCursor = val.substring(0, cursorPosition);

    const match = textBeforeCursor.match(/@\w*$/);

    if (match) {
      showMentionSuggestions();
    } else {
      closeMentionSuggestions();
    }
  });

  document.addEventListener("click", (e) => {
    if (!e.target.closest("#mention-suggestions") && e.target !== input) {
      closeMentionSuggestions();
    }
    document.querySelectorAll('[id^="menu-dropdown-"]').forEach((el) => {
      el.classList.add("hidden");
    });
  });

  input.addEventListener("paste", (e) => {
    const items = (e.clipboardData || e.originalEvent.clipboardData).items;
    let imageCount = 0;
    for (let index in items) {
      if (
        items[index].kind === "file" &&
        items[index].type.includes("image/")
      ) {
        imageCount++;
      }
    }

    if (state.uploadedImages.length + imageCount > 10) {
      e.preventDefault();
      showToast("You can only send up to 10 images at a time!", "warning");
      return;
    }

    for (let index in items) {
      const item = items[index];
      if (item.kind === "file" && item.type.includes("image/")) {
        const blob = item.getAsFile();
        previewImage(blob);
      }
    }
  });
}

function showMentionSuggestions() {
  state.selectedMentionIndex = 0;
  renderMentionSuggestions();
  document.getElementById("mention-suggestions").classList.remove("hidden");
}

function renderMentionSuggestions() {
  const container = document.getElementById("mention-suggestions");
  const isSelected = state.selectedMentionIndex === 0;

  container.innerHTML = `
        <div onclick="insertMention('ask')" class="px-4 py-3 cursor-pointer flex items-center gap-3 transition border-b border-slate-50 last:border-0 ${
          isSelected ? "bg-blue-50" : "hover:bg-slate-50"
        }">
            <div class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary"><i class="fa-solid fa-robot"></i></div>
            <div class="flex flex-col">
                <span class="font-bold text-sm text-slate-700">@ask</span>
                <span class="text-[10px] text-slate-400 font-bold">Hỏi AI Bot</span>
            </div>
        </div>
    `;
}

function closeMentionSuggestions() {
  document.getElementById("mention-suggestions").classList.add("hidden");
}

window.insertMention = function (tag) {
  const input = document.getElementById("chat-input");
  const val = input.value;
  const cursorPosition = input.selectionStart;
  const textBeforeCursor = val.substring(0, cursorPosition);
  const textAfterCursor = val.substring(cursorPosition);

  const newTextBefore = textBeforeCursor.replace(/@\w*$/, "@" + tag + " ");

  input.value = newTextBefore + textAfterCursor;
  input.focus();
  closeMentionSuggestions();
};

window.handleFileUpload = function (input) {
  if (input.files && input.files.length > 0) {
    if (state.uploadedImages.length + input.files.length > 10) {
      showToast("You can only send up to 10 images at a time!", "warning");
      input.value = "";
      return;
    }
    Array.from(input.files).forEach((file) => previewImage(file));
  }
  input.value = "";
};

function previewImage(file) {
  const reader = new FileReader();
  reader.onload = function (e) {
    const img = new Image();
    img.src = e.target.result;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const MAX_WIDTH = 800;
      const MAX_HEIGHT = 800;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
      state.uploadedImages.push(dataUrl);
      renderImagePreviews();
    };
  };
  reader.readAsDataURL(file);
}

function renderImagePreviews() {
  const area = document.getElementById("image-preview-area");
  if (state.uploadedImages.length > 0) {
    area.classList.remove("hidden");
    area.innerHTML = "";
    state.uploadedImages.forEach((src, idx) => {
      area.innerHTML += `
                <div class="relative w-20 h-20 shrink-0">
                    <img src="${src}" class="w-full h-full object-cover rounded-lg border border-slate-300">
                    <button onclick="removeImage(${idx})" class="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"><i class="fa-solid fa-xmark"></i></button>
                </div>
            `;
    });
  } else {
    area.classList.add("hidden");
  }
}

window.removeImage = function (idx) {
  state.uploadedImages.splice(idx, 1);
  renderImagePreviews();
};

window.handleSendMessage = function () {
  if (!socket.connected) {
    showToast("Connection lost. Please wait...", "error");
    return;
  }

  const input = document.getElementById("chat-input");
  const text = input.value.trim();
  const currentClass = state.classes.find((c) => c.id === state.activeChatId);

  if (!currentClass.chatEnabled) {
    if (state.currentUser && state.currentUser.role !== "teacher") {
      alert("Teacher has disabled chat!");
      return;
    }
  }

  if (!text && state.uploadedImages.length === 0) return;

  const newMsg = {
    classId: state.activeChatId,
    text: text,
    images: [...state.uploadedImages],
    replyTo: state.replyingTo
      ? {
          id: state.replyingTo.id,
          name: state.replyingTo.name,
          text: state.replyingTo.text,
        }
      : null,
  };

  socket.emit("send_message", newMsg);

  input.value = "";
  state.uploadedImages = [];
  cancelReply();
  renderImagePreviews();
};

window.toggleSearch = function () {
  const bar = document.getElementById("chat-search-bar");
  const btn = document.getElementById("btn-search-toggle");
  const input = document.getElementById("search-input");

  if (bar.classList.contains("hidden")) {
    bar.classList.remove("hidden");
    bar.classList.add("flex");
    btn.classList.add("hidden");
    input.focus();
  } else {
    bar.classList.add("hidden");
    bar.classList.remove("flex");
    btn.classList.remove("hidden");
    state.searchTerm = "";
    input.value = "";
    renderMessages();
  }
};

window.handleSearch = function (val) {
  state.searchTerm = val.trim();
  renderMessages();
};

window.replyMessage = function (msgId) {
  const msg = state.messages.find((m) => m.id === msgId);
  if (msg) {
    state.replyingTo = msg;
    const preview = document.getElementById("reply-preview");
    const content = document.getElementById("reply-content");
    content.innerText = `Replying to ${msg.name}: "${msg.text.substring(
      0,
      40
    )}${msg.text.length > 40 ? "..." : ""}"`;
    preview.classList.remove("hidden");
    document.getElementById("chat-input").focus();
  }
};

window.cancelReply = function () {
  state.replyingTo = null;
  document.getElementById("reply-preview").classList.add("hidden");
};

window.showMessageContextMenu = function (event, msgId) {
  event.preventDefault();
  event.stopPropagation();

  window.closeContextMenu(); // Close any open context menu

  const menu = document.getElementById(`context-menu-${msgId}`);
  if (!menu) return;

  // Calculate position with overflow protection
  let left = event.pageX;
  let top = event.pageY;

  // Get menu dimensions (show temporarily to measure)
  menu.classList.remove("hidden");
  const menuRect = menu.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Adjust horizontal position if overflow
  if (left + menuRect.width > viewportWidth) {
    left = viewportWidth - menuRect.width - 10;
  }

  // Adjust vertical position if overflow
  if (top + menuRect.height > viewportHeight) {
    top = viewportHeight - menuRect.height - 10;
  }

  // Position the menu
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;

  // Store current menu ID and timestamp
  window.currentContextMenuId = `context-menu-${msgId}`;
  window.contextMenuOpenTime = Date.now();
};

window.closeContextMenu = function () {
  if (window.currentContextMenuId) {
    const menu = document.getElementById(window.currentContextMenuId);
    if (menu) menu.classList.add("hidden");
    window.currentContextMenuId = null;
    window.contextMenuOpenTime = null;
  }
};

// Setup event listeners only once - using a more reliable check
(function setupContextMenuListeners() {
  if (window.__contextMenuListenersAdded) return;
  window.__contextMenuListenersAdded = true;

  // Add event delegation for context menu on messages
  document.addEventListener(
    "contextmenu",
    (e) => {
      const messageItem = e.target.closest(".message-item");
      if (messageItem) {
        const msgId = messageItem.dataset.messageId;
        if (msgId) {
          window.showMessageContextMenu(e, msgId);
        }
      } else if (!e.target.closest('[id^="msg-"]')) {
        window.closeContextMenu();
      }
    },
    false
  );

  // Close context menu when clicking outside
  document.addEventListener(
    "click",
    (e) => {
      const timeSinceOpen = window.contextMenuOpenTime
        ? Date.now() - window.contextMenuOpenTime
        : 999;

      // Don't close if menu was just opened (within 150ms)
      if (timeSinceOpen < 150) {
        return;
      }

      // Don't close if clicking inside the menu
      if (e.target.closest('[id^="context-menu-"]')) {
        return;
      }

      window.closeContextMenu();
    },
    false
  );
})();

// Reactions removed per user request

window.toggleMessageMenu = function (msgId, event) {
  event.stopPropagation();
  const menu = document.getElementById(`menu-dropdown-${msgId}`);

  document.querySelectorAll('[id^="menu-dropdown-"]').forEach((el) => {
    if (el.id !== `menu-dropdown-${msgId}`) el.classList.add("hidden");
  });

  if (menu) {
    menu.classList.toggle("hidden");
  }
};

window.deleteMessage = function (msgId) {
  Swal.fire({
    title: "Delete message?",
    text: "This message will be permanently deleted.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#64748b",
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "Cancel",
    heightAuto: false,
    returnFocus: false,
  }).then((result) => {
    if (result.isConfirmed) {
      socket.emit("delete_message", msgId);
    }
  });
};

window.copyMessage = function (msgId) {
  const msg = state.messages.find((m) => m.id === msgId);
  if (msg && !msg.recalled) {
    navigator.clipboard.writeText(msg.text).then(() => {
      showToast("Message copied!");
    });
  }
};

window.pinMessage = function (msgId) {
  const msg = state.messages.find((m) => m.id === msgId);
  if (!msg || msg.deleted) {
    showToast("Message not found", "error");
    return;
  }

  // Check if already pinned
  if (
    state.pinnedMessages &&
    state.pinnedMessages.some((p) => p.messageId === msgId)
  ) {
    showToast("Message already pinned", "info");
    return;
  }

  socket.emit("pin_message", { messageId: msgId, classId: state.activeChatId });
  showToast("Pinning message...", "info");
};

window.unpinMessage = function (msgId) {
  socket.emit("unpin_message", {
    messageId: msgId,
    classId: state.activeChatId,
  });
};

window.togglePinnedList = function () {
  const list = document.getElementById("pinned-messages-list");
  if (list) {
    list.classList.toggle("hidden");
  }
};

function renderPinnedMessages() {
  const area = document.getElementById("pinned-messages-area");
  const list = document.getElementById("pinned-messages-list");
  const countEl = document.getElementById("pinned-count");
  const previewEl = document.getElementById("pinned-preview");

  if (!state.pinnedMessages || !state.activeChatId) {
    if (area) area.classList.add("hidden");
    return;
  }

  const classPinned = state.pinnedMessages.filter(
    (p) => p.classId === state.activeChatId
  );

  if (classPinned.length === 0) {
    area.classList.add("hidden");
    return;
  }

  // Show pinned area
  area.classList.remove("hidden");
  countEl.textContent =
    classPinned.length === 1
      ? "1 Pinned Message"
      : `${classPinned.length} Pinned Messages`;

  // Show preview of most recent pinned message
  const latestPinned = classPinned[classPinned.length - 1];
  const msg = state.messages.find((m) => m.id === latestPinned.messageId);
  if (msg) {
    previewEl.textContent = msg.text
      ? msg.text.substring(0, 50) + (msg.text.length > 50 ? "..." : "")
      : "[Image]";
  }

  // Render pinned messages list
  list.innerHTML = "";
  classPinned.forEach((pinned) => {
    const msg = state.messages.find((m) => m.id === pinned.messageId);
    if (!msg) return;

    const avatar =
      msg.type === "teacher"
        ? "https://api.dicebear.com/7.x/notionists/svg?seed=Teacher"
        : `https://api.dicebear.com/7.x/notionists/svg?seed=${msg.name}`;

    list.innerHTML += `
            <div class="p-3 hover:bg-slate-50 border-b border-slate-100 flex gap-3 items-start group">
                <img src="${avatar}" class="w-8 h-8 rounded-full shrink-0">
                <div class="flex-1 min-w-0">
                    <div class="font-bold text-sm text-slate-700">${
                      msg.name
                    }</div>
                    <div class="text-sm text-slate-600 break-words" onclick="scrollToMessage('${
                      msg.id
                    }'); togglePinnedList();">
                        ${msg.text || "[Image]"}
                    </div>
                    <div class="text-xs text-slate-400 mt-1">${new Date(
                      msg.createdAt
                    ).toLocaleString()}</div>
                </div>
                ${
                  state.currentUser && state.currentUser.role === "teacher"
                    ? `
                    <button onclick="unpinMessage('${msg.id}')" class="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition shrink-0">
                        <i class="fa-solid fa-times"></i>
                    </button>
                `
                    : ""
                }
            </div>
        `;
  });
}

function renderMessages(forceScroll = false) {
  const container = document.getElementById("messages-container");

  const isNearBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight < 100;

  container.innerHTML = "";

  // Filter messages: exclude deleted, and exclude messages that reply to non-existent (deleted/recalled) messages
  let msgs = state.messages.filter((m) => {
    if (m.classId !== state.activeChatId) return false;
    if (m.deleted) return false;

    // If this message replies to another message, check if that message still exists
    if (m.replyTo && m.replyTo.id) {
      const replyToExists = state.messages.some(
        (msg) => msg.id === m.replyTo.id && !msg.deleted
      );
      if (!replyToExists) return false;
    }

    return true;
  });

  if (state.searchTerm) {
    const term = state.searchTerm.toLowerCase();
    msgs = msgs.filter(
      (m) =>
        (m.text && m.text.toLowerCase().includes(term)) ||
        (m.name && m.name.toLowerCase().includes(term))
    );
  }

  let lastReadIndex = -1;
  if (state.currentUser) {
    const receipt = state.readReceipts.find(
      (r) =>
        r.class_id === state.activeChatId && r.user_id === state.currentUser.id
    );
    if (receipt && receipt.last_read_msg_id) {
      lastReadIndex = msgs.findIndex((m) => m.id === receipt.last_read_msg_id);
    }
  }

  msgs.forEach((msg, index) => {
    // Insert Unread Line - Logic Updated: Don't show if near bottom (user is reading)
    if (lastReadIndex !== -1 && index === lastReadIndex + 1 && !isNearBottom) {
      container.innerHTML += `
                <div class="w-full text-center text-xs text-red-500 my-4 flex items-center gap-2 opacity-80">
                    <div class="h-px bg-red-200 flex-1"></div>
                    <span class="font-bold">New Messages</span>
                    <div class="h-px bg-red-200 flex-1"></div>
                </div>
            `;
    }

    let isMe = false;
    if (state.currentUser) {
      if (state.currentUser.role === "teacher") {
        isMe = msg.type === "teacher";
      } else {
        if (msg.senderId) {
          isMe = msg.senderId === state.currentUser.id;
        } else {
          isMe = msg.type === "student" && msg.name === state.currentUser.name;
        }
      }
    }

    const isBot = msg.type === "bot";

    let avatar = isBot
      ? `https://api.dicebear.com/7.x/bottts/svg?seed=AI`
      : isMe
      ? `https://api.dicebear.com/7.x/notionists/svg?seed=Teacher`
      : `https://api.dicebear.com/7.x/fun-emoji/svg?seed=${msg.name}`;

    let displayHtml = msg.text.replace(
      /(@\w+)/g,
      '<span class="mention-tag">$1</span>'
    );

    if (isBot) {
      displayHtml = marked.parse(msg.text);
    }

    if (state.searchTerm) {
      const term = state.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${term})`, "gi");
      displayHtml = displayHtml.replace(
        regex,
        '<mark class="bg-yellow-200 text-slate-800 rounded px-0.5">$1</mark>'
      );
    }

    displayHtml = displayHtml.replace(
      /<span class="mention-tag">@ask<\/span>/g,
      '<span class="bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-bold border border-blue-200 inline-flex items-center gap-1"><i class="fa-solid fa-robot text-xs"></i> @ask</span>'
    );

    if (msg.recalled) {
      displayHtml = `<span class="italic text-slate-400 text-xs border border-slate-200 px-2 py-1 rounded-lg bg-slate-50 inline-block">${msg.text}</span>`;
    }

    let imgHtml = "";
    if (msg.images && msg.images.length > 0) {
      imgHtml = `<div class="flex flex-wrap gap-1 mt-2">`;
      msg.images.forEach((src) => {
        imgHtml += `<img src="${src}" onclick="openLightbox('${src}')" class="max-w-[150px] max-h-[150px] object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90 transition">`;
      });
      imgHtml += `</div>`;
    }

    let replyHtml = "";
    if (msg.replyTo) {
      replyHtml = `
                <div onclick="scrollToMessage('${
                  msg.replyTo.id
                }')" class="cursor-pointer mb-1 pl-2 border-l-2 ${
        isMe
          ? "border-white/50 text-white/80"
          : "border-slate-300 text-slate-400"
      } text-xs truncate opacity-75 hover:opacity-100 transition-opacity" title="Click to view original message">
                    <span class="font-bold">${msg.replyTo.name}</span>: ${
        msg.replyTo.text
      }
                </div>
            `;
    }

    // Context Menu HTML
    const contextMenuId = `context-menu-${msg.id}`;
    const contextMenuHtml = `
            <div id="${contextMenuId}" class="hidden fixed bg-white shadow-2xl border border-slate-200 rounded-xl py-2 z-[9999] min-w-[180px] animate-fade-in">
                <button onclick="replyMessage('${
                  msg.id
                }'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-blue-600 flex items-center gap-3 text-sm transition">
                    <i class="fa-solid fa-reply w-4"></i> Reply
                </button>
                ${
                  state.currentUser && state.currentUser.role === "teacher"
                    ? `<button onclick="pinMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-yellow-50 text-yellow-600 flex items-center gap-3 text-sm transition">
                        <i class="fa-solid fa-thumbtack w-4"></i> Pin Message
                    </button>`
                    : ""
                }
                ${
                  isMe && !msg.recalled
                    ? `<button onclick="recallMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-orange-50 text-orange-600 flex items-center gap-3 text-sm transition border-t border-slate-100">
                        <i class="fa-solid fa-rotate-left w-4"></i> Recall
                    </button>`
                    : ""
                }
                ${
                  isMe && !msg.recalled
                    ? `<button onclick="deleteMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-red-50 text-red-600 flex items-center gap-3 text-sm transition">
                        <i class="fa-solid fa-trash w-4"></i> Delete
                    </button>`
                    : ""
                }
                ${
                  !isMe &&
                  !msg.recalled &&
                  state.currentUser &&
                  state.currentUser.role === "teacher"
                    ? `<button onclick="adminRecallMessage('${msg.id}'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-red-50 text-red-600 flex items-center gap-3 text-sm transition border-t border-slate-100">
                        <i class="fa-solid fa-ban w-4"></i> Remove
                    </button>`
                    : ""
                }
                <button onclick="copyMessage('${
                  msg.id
                }'); closeContextMenu();" class="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-slate-600 flex items-center gap-3 text-sm transition border-t border-slate-100">
                    <i class="fa-regular fa-copy w-4"></i> Copy
                </button>
            </div>
        `;

    container.innerHTML += `
            <div id="msg-${msg.id}" data-message-id="${
      msg.id
    }" class="message-item flex gap-3 ${
      isMe ? "flex-row-reverse" : ""
    } animate-fade-in mb-3 group items-end transition-colors duration-1000 rounded-xl p-1 relative">
                <img src="${avatar}" class="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-100 shrink-0">
                <div class="max-w-[75%] flex flex-col ${
                  isMe ? "items-end" : "items-start"
                } relative">
                    <div class="text-[10px] text-slate-400 mb-1 px-1 flex ${
                      isMe ? "flex-row-reverse" : ""
                    } gap-1">
                        <span class="font-bold text-slate-600">${
                          msg.name
                        }</span>
                    </div>
                    
                    <div class="flex items-center gap-2 ${
                      isMe ? "flex-row-reverse" : ""
                    } relative">
                        <div class="${
                          isMe
                            ? "bg-primary text-white rounded-tr-none"
                            : "bg-white text-slate-600 rounded-tl-none"
                        } p-3 rounded-2xl shadow-sm text-sm break-words transition-shadow relative">
                            ${replyHtml}
                            ${displayHtml}
                            ${imgHtml}
                        </div>
                    </div>
                </div>
            </div>
            ${!msg.recalled ? contextMenuHtml : ""}
        `;
  });

  setTimeout(() => {
    if (forceScroll || isNearBottom) {
      window.scrollToBottom();
    }
  }, 100);
}

window.scrollToBottom = function () {
  const container = document.getElementById("messages-container");
  container.scrollTop = container.scrollHeight;
};

window.openLightbox = function (src) {
  const modal = document.getElementById("modal-lightbox");
  const img = document.getElementById("lightbox-img");
  img.src = src;
  modal.classList.remove("hidden");
};

window.closeLightbox = function () {
  document.getElementById("modal-lightbox").classList.add("hidden");
};

window.openMediaHistory = function () {
  const modal = document.getElementById("modal-media-history");
  const grid = document.getElementById("media-history-grid");
  grid.innerHTML = "";

  const msgs = state.messages.filter((m) => m.classId === state.activeChatId);
  const allImages = [];
  msgs.forEach((m) => {
    if (m.images) allImages.push(...m.images);
  });

  if (allImages.length === 0) {
    grid.innerHTML =
      '<div class="col-span-full text-center text-slate-400 py-8 flex flex-col items-center"><i class="fa-regular fa-images text-4xl mb-2 opacity-50"></i><span>No images sent yet</span></div>';
  } else {
    allImages.forEach((src) => {
      grid.innerHTML += `
                <div onclick="openLightbox('${src}')" class="aspect-square cursor-pointer overflow-hidden rounded-lg border border-slate-200 hover:opacity-80 transition">
                    <img src="${src}" class="w-full h-full object-cover">
                </div>
            `;
    });
  }
  modal.classList.remove("hidden");
};

window.openChatInfo = function () {
  const modal = document.getElementById("modal-chat-info");
  renderChatInfoList();
  modal.classList.remove("hidden");
};

function renderChatInfoList() {
  const list = document.getElementById("chat-info-list");
  const count = document.getElementById("chat-info-count");
  list.innerHTML = "";

  const classStudents = state.students.filter(
    (s) => s.classId === state.activeChatId
  );
  count.innerText = `(${classStudents.length + 1})`;

  const teacherOnline = state.onlineUserIds.some((id) =>
    id.startsWith("teacher_")
  );
  list.innerHTML += `
        <div class="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50">
            <div class="relative">
                <img src="https://api.dicebear.com/7.x/notionists/svg?seed=Teacher" class="w-10 h-10 rounded-full bg-white border border-slate-200">
                ${
                  teacherOnline
                    ? '<div class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>'
                    : ""
                }
            </div>
            <div>
                <div class="font-bold text-sm text-slate-700">Teacher</div>
                <div class="text-[10px] text-slate-400 font-bold">Admin</div>
            </div>
        </div>
    `;

  classStudents.forEach((s) => {
    const isOnline = state.onlineUserIds.includes(`student_${s.id}`);
    console.log(s);
    list.innerHTML += `
            <div class="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50">
                <div class="relative">
                    <img src="https://api.dicebear.com/7.x/fun-emoji/svg?seed=${
                      s.seed
                    }" class="w-10 h-10 rounded-full bg-white border border-slate-200">
                    ${
                      isOnline
                        ? '<div class="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>'
                        : ""
                    }
                </div>
                <div>
                    <div class="font-bold text-sm text-slate-700">${
                      s.name
                    }</div>
                    <div class="text-[10px] text-slate-400 font-bold">Student</div>
                </div>
            </div>
        `;
  });
}

window.toggleChatSettings = function () {
  const modal = document.getElementById("modal-chat-settings");
  const btn = document.getElementById("btn-chat-perm");

  const currentClass = state.classes.find((c) => c.id === state.activeChatId);
  if (currentClass) {
    updateChatPermUI(currentClass.chatEnabled);
  }

  modal.classList.remove("hidden");
};

window.toggleChatPermission = function () {
  socket.emit("toggle_chat", state.activeChatId);
  const currentClass = state.classes.find((c) => c.id === state.activeChatId);
  if (currentClass) {
    updateChatPermUI(!currentClass.chatEnabled);
  }
};

function updateChatPermUI(enabled) {
  const btn = document.getElementById("btn-chat-perm");
  const knob = btn.firstElementChild;

  if (enabled) {
    btn.classList.remove("bg-slate-200");
    btn.classList.add("bg-green-500");
    knob.classList.add("translate-x-6");
  } else {
    btn.classList.add("bg-slate-200");
    btn.classList.remove("bg-green-500");
    knob.classList.remove("translate-x-6");
  }
}

function checkChatPermissionUI() {
  const notice = document.getElementById("chat-disabled-notice");
  const inputWrapper = document.getElementById("chat-input-wrapper");

  if (state.activeChatId) {
    const currentClass = state.classes.find((c) => c.id === state.activeChatId);
    if (currentClass && !currentClass.chatEnabled) {
      if (state.currentUser && state.currentUser.role === "student") {
        notice.classList.remove("hidden");
        inputWrapper.classList.add("opacity-50", "pointer-events-none");
      } else {
        notice.classList.add("hidden");
        inputWrapper.classList.remove("opacity-50", "pointer-events-none");
      }
    } else {
      notice.classList.add("hidden");
      inputWrapper.classList.remove("opacity-50", "pointer-events-none");
    }
  }
}

window.scrollToMessage = function (msgId) {
  const el = document.getElementById(`msg-${msgId}`);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Add highlight animation without triggering re-layout
    const originalBg = el.style.backgroundColor;
    el.style.backgroundColor = "#fef3c7";
    el.style.transition = "background-color 0.3s";
    setTimeout(() => {
      el.style.backgroundColor = originalBg;
      setTimeout(() => {
        el.style.backgroundColor = "";
        el.style.transition = "";
      }, 300);
    }, 1500);
  } else {
    showToast("Message not found (might be too old)", "info");
  }
};

// Initialize the application
document.addEventListener("DOMContentLoaded", init);
